import './eojr.css'
import clsx from 'clsx'
import Chart from 'react-apexcharts'
import { format, parseISO } from 'date-fns'
import { Fragment, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LogoText } from '~/assets/LogoText'
import {
  ChartItem,
  LeadershipValue,
  RespondentOpenEndedAnswer,
  Score,
  SelfReportedFeedback,
} from '../hupoperspectives/components/EndOfJourneyReport'
import { useParams, useSearchParams } from 'react-router-dom'
import { UserProfile } from '~/types/api'
import { FUJIFILM_COMPANY_ID } from '../../app/constants'
import { useHideChatlio } from '../../hooks/useHideChatlio'

const CURRENT_YEAR = new Date().getFullYear()
const GROUPS = ['Self', 'Manager(s)', 'Peer(s)', 'Reportee(s)'] as const
const SCORE_LEGEND = [
  { value: 1, label: 'N/A (No opportunity to demonstrate)' },
  { value: 2, label: 'Rarely seen' },
  { value: 3, label: 'Seen a few times' },
  { value: 4, label: 'Seen numerous times' },
  { value: 5, label: 'Consistently demonstrates' },
  { value: 6, label: 'Admired as a role model' },
] as const

const FUJI_SCORE_LEGEND = [
  { value: 1, label: 'Demonstrated ≤ 1 indicator' },
  { value: 2, label: 'Demonstrated 2 indicators' },
  { value: 3, label: 'Demonstrated 3 indicators' },
  { value: 4, label: 'Demonstrated 4 indicators' },
  { value: 5, label: 'Demonstrated all indicators consistently' },
] as const

interface BasePageProps {
  className?: string
  pageNo?: number
  otherCompanyLogo: string
  otherCompanyLogoHeight?: number
  name: string
  dateRange: string
}

interface ChartDataSeries {
  name: string
  data: number[] | undefined
}

interface FeedbackItem {
  role: string
  scoreComments: Score[]
}

interface PersonalReflection {
  question: string
  answer: string
}

// Split Page2 content for overflow
const splitPage2Content = (
  themes: string[],
  selfInitialAnswers: any[],
  groupedLeadershipValues: Record<string, string[]>
) => {
  const maxHeight = 800 // Max height for right column content
  const pages: Array<{
    themes: string[]
    selfInitialAnswers: any[]
    groupedLeadershipValues: Record<string, string[]>
  }> = []

  // Always show organizational DNA on first page if selfInitialAnswers exist
  const showDNAOnLeft = selfInitialAnswers?.length > 0

  // First page - always include some content
  let currentHeight = 0
  let currentThemes: string[] = []
  const currentAnswers: any[] = []

  // Add initial answers first (priority content)
  if (selfInitialAnswers?.length > 0) {
    for (const answer of selfInitialAnswers) {
      const answerHeight = 40 + (answer.responses?.[0]?.length || 0) * 0.3
      if (currentHeight + answerHeight < maxHeight * 0.6) {
        // Reserve space for themes
        currentAnswers.push(answer)
        currentHeight += answerHeight
      } else {
        break
      }
    }
  }

  // Add themes if there's space
  if (themes.length > 0 && currentHeight < maxHeight * 0.8) {
    const availableHeight = maxHeight - currentHeight - 80 // Reserve for header
    const maxThemes = Math.floor(availableHeight / 60)
    currentThemes = themes.slice(0, Math.max(1, maxThemes)) // At least 1 theme
  }

  // First page
  pages.push({
    themes: currentThemes,
    selfInitialAnswers: currentAnswers,
    groupedLeadershipValues: showDNAOnLeft ? groupedLeadershipValues : {},
  })

  // Additional pages for remaining content
  const remainingThemes = themes.slice(currentThemes.length)
  const remainingAnswers =
    selfInitialAnswers?.slice(currentAnswers.length) || []

  if (remainingThemes.length > 0 || remainingAnswers.length > 0) {
    // Calculate how much can fit on subsequent pages
    let pageThemes: string[] = []
    let pageAnswers: any[] = []
    let pageHeight = 0

    // Add remaining answers
    for (const answer of remainingAnswers) {
      const answerHeight = 40 + (answer.responses?.[0]?.length || 0) * 0.3
      if (pageHeight + answerHeight < maxHeight) {
        pageAnswers.push(answer)
        pageHeight += answerHeight
      } else if (pageAnswers.length > 0) {
        // Create page and start new one
        pages.push({
          themes: [],
          selfInitialAnswers: pageAnswers,
          groupedLeadershipValues,
        })
        pageAnswers = [answer]
        pageHeight = answerHeight
      }
    }

    // Add remaining themes
    for (const theme of remainingThemes) {
      if (pageHeight + 60 < maxHeight) {
        pageThemes.push(theme)
        pageHeight += 60
      } else if (pageThemes.length > 0 || pageAnswers.length > 0) {
        // Create page and start new one
        pages.push({
          themes: pageThemes,
          selfInitialAnswers: pageAnswers,
          groupedLeadershipValues,
        })
        pageThemes = [theme]
        pageAnswers = []
        pageHeight = 60
      }
    }

    // Add final page if there's remaining content
    if (pageThemes.length > 0 || pageAnswers.length > 0) {
      pages.push({
        themes: pageThemes,
        selfInitialAnswers: pageAnswers,
        groupedLeadershipValues,
      })
    }
  }

  return pages
}

// Shared components
const PageIndicator: React.FC<{ pageNo: number }> = ({ pageNo }) => (
  <div className="absolute top-4 right-6 gap-3 flex items-center">
    <div className="bg-[#95989B] w-[1.5px] h-5" />
    <div className="text-[#95989b]">{pageNo}</div>
  </div>
)

const PageHeader: React.FC<{
  dateRange: string
  name: string
  title?: string
}> = ({ dateRange, name, title }) => (
  <div className="flex px-8 justify-between items-start mb-6">
    <div>
      <p className="text-gray-500 text-sm">{dateRange}</p>
      <h1 className="text-[#ff4b0a] text-4xl font-bold mt-2">{name}</h1>
      {title && (
        <p className="text-gray-800 mt-6 text-xl font-medium">{title}</p>
      )}
    </div>
  </div>
)

const Footer: React.FC<{
  otherCompanyLogo: string
  otherCompanyLogoHeight?: number
}> = ({ otherCompanyLogo, otherCompanyLogoHeight = 14 }) => (
  <div className="absolute bottom-0 right-0 p-4 flex gap-2 justify-end items-center">
    <img
      className={`w-${otherCompanyLogoHeight} h-auto object-contain`}
      src={otherCompanyLogo}
      alt="Company logo"
    />
    <LogoText className="w-12 h-auto" />
  </div>
)

const ChartLegend: React.FC = () => (
  <div className="flex items-center justify-center gap-8">
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded-sm bg-[#3b8cee]" />
      <span className="text-sm font-medium">Start of Journey result</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded-sm bg-[#ff4b0a]" />
      <span className="text-sm font-medium">End of Journey result</span>
    </div>
  </div>
)

const getScoreLegend = (userCompany?: string) => {
  return userCompany === FUJIFILM_COMPANY_ID ? FUJI_SCORE_LEGEND : SCORE_LEGEND
}

const ScoreLegend: React.FC<{ size?: 'sm' | 'xs'; userCompany?: string }> = ({
  size = 'sm',
  userCompany,
}) => (
  <div
    className={`flex flex-wrap justify-center gap-x-${
      size === 'xs' ? '4' : '8'
    } gap-y-2 text-${size} text-gray-600`}
  >
    {getScoreLegend(userCompany).map((item, i) => (
      <div key={i} className="flex items-center">
        <span className={`font-${size === 'xs' ? 'medium' : 'semibold'} mr-1`}>
          {item.value}:
        </span>
        <span>{item.label}</span>
      </div>
    ))}
  </div>
)

// Page components
const FrontCover: React.FC<BasePageProps & { isDraft?: boolean }> = ({
  className,
  otherCompanyLogo,
  name,
  dateRange,
  otherCompanyLogoHeight = 14,
  isDraft = false,
}) => (
  <div
    className={clsx(
      'relative bg-[url(/eojr-assets/cover-bg.png)] text-white p-8 flex flex-col justify-between',
      className
    )}
  >
    {isDraft && (
      <div className="absolute inset-0 flex items-center justify-center rotate-45 text-[128px] font-bold opacity-20 pointer-events-none">
        DRAFT
      </div>
    )}
    <div className="flex gap-4">
      <LogoText className="w-12 h-auto" color="#fff" />
      <img
        className={`w-${otherCompanyLogoHeight} h-auto object-contain`}
        src={otherCompanyLogo}
        alt="Company logo"
      />
    </div>

    <div className="flex-1 flex flex-col justify-center mx-8">
      <div className="mb-8">
        <div className="text-lg leading-5 tracking-[3px] mb-4">
          HUPO PERSPECTIVES
        </div>
        <h1 className="text-5xl font-bold mb-4 leading-tight">
          Your Hupo Coaching Journey
        </h1>
        <div className="text-xl mb-8">Personal Reflections & 360 Feedback</div>
      </div>

      <div className="mb-12">
        <div className="text-2xl font-bold mb-2 uppercase">{name}</div>
        <div className="text-xl font-bold uppercase">{dateRange}</div>
      </div>

      <div className="space-y-4 text-base leading-relaxed max-w-md">
        <p>
          This report contains the collated feedback from several of your
          colleagues as well as your own reflections. It's intended to support
          your personal growth and ongoing development as a leader.
        </p>
        <p>
          When reviewing the content, please remember that you have asked your
          colleagues for their honest, frank feedback about how they perceive
          your behaviour, in relation to the Leadership DNA and Values set forth
          and championed by your organisation.
        </p>
        <p>
          Pay attention to the broader themes in the feedback, as well as
          general trends, and even discrepancies. Different perceptions are
          normal, since different stakeholders see you in different contexts.
          Let's dive in.
        </p>
      </div>
    </div>
  </div>
)

const Page2: React.FC<
  BasePageProps & {
    completedSessions: number
    coach?: UserProfile
    themes: string[]
    groupedLeadershipValues: Record<string, string[]>
    selfInitialAnswers: any
  }
> = ({
  className,
  pageNo = 2,
  otherCompanyLogo,
  name,
  dateRange,
  completedSessions,
  coach,
  themes,
  groupedLeadershipValues,
  selfInitialAnswers,
}) => {
  return (
    <div className={clsx('flex gap-8 pr-8', className)}>
      <PageIndicator pageNo={pageNo} />

      <div className="flex-1 p-8 bg-[#F6F8F8]">
        <div className="text-[#95989b] text-sm uppercase">{dateRange}</div>
        <h1 className="mt-2 text-[#ff4b0a] text-3xl font-bold">{name}</h1>

        <div className="mt-10 space-y-8">
          {/* Sessions Completed */}
          <div>
            <h3 className="text-[#161618] text-lg mb-2">Sessions completed</h3>
            <div className="text-[#1c7aeb] text-6xl font-bold">
              {completedSessions}
            </div>
            <div className="border-b border-dotted border-[#c7cbce] mt-4" />
          </div>

          {/* Your Coach */}
          {coach && (
            <div>
              <h3 className="text-[#161618] text-lg mb-4">Your coach</h3>
              <div className="flex items-start gap-4 mb-4">
                <img
                  src={coach.picture}
                  alt={coach.name}
                  className="rounded-full size-24 object-cover"
                />
              </div>
              <h4 className="text-[#1c7aeb] text-xl font-bold">{coach.name}</h4>
              <p className="text-[#58595a] text-sm mt-1">{coach.title}</p>
              {Object.keys(groupedLeadershipValues).length > 0 && (
                <div className="border-b border-dotted border-[#c7cbce] mt-6" />
              )}
            </div>
          )}

          {selfInitialAnswers?.length > 0 && (
            <OrganizationalDNA
              groupedLeadershipValues={groupedLeadershipValues}
            />
          )}
        </div>
      </div>

      <div className="mt-[100px] flex-[2] space-y-8">
        {/* Personal Reflections */}
        {selfInitialAnswers?.length > 0 && (
          <div>
            <h2 className="text-[#161618] text-xl font-medium mb-6">
              At the start of your coaching journey, you shared these personal
              reflections:
            </h2>

            <div className="space-y-6">
              {selfInitialAnswers.map((item: any, index: number) => (
                <div key={index}>
                  <h3 className="text-[#161618] font-medium mb-2">
                    {item.question}
                  </h3>
                  <p className="text-[#58595a] text-sm leading-relaxed">
                    {item.responses[0]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Themes */}
        {themes.length > 0 && (
          <div>
            <h2 className="text-[#161618] text-xl font-medium mb-6">
              As your coaching progressed, these were the key themes you
              explored:
            </h2>

            <div className="space-y-4">
              {themes.map((theme, index) => (
                <div key={index} className="bg-[#F6F8F8] p-4 rounded-lg">
                  <p className="text-[#58595a] text-center">{theme}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {selfInitialAnswers?.length === 0 && (
          <div className="border-t border-dotted border-[#c7cbce] mt-6 pt-6">
            <OrganizationalDNA
              groupedLeadershipValues={groupedLeadershipValues}
            />
          </div>
        )}
      </div>

      <Footer otherCompanyLogo={otherCompanyLogo} />
    </div>
  )
}

// New component for Page2 overflow pages
const Page2Overflow: React.FC<
  BasePageProps & {
    themes: string[]
    selfInitialAnswers: any[]
    groupedLeadershipValues: Record<string, string[]>
  }
> = ({
  className,
  pageNo,
  otherCompanyLogo,
  name,
  dateRange,
  themes,
  selfInitialAnswers,
  groupedLeadershipValues,
}) => (
  <div className={clsx('flex gap-8 pr-8', className)}>
    <PageIndicator pageNo={pageNo ?? 3} />

    <div className="flex-1 p-8 bg-[#F6F8F8]">
      <div className="text-[#95989b] text-sm uppercase">{dateRange}</div>
      <h1 className="mt-2 text-[#ff4b0a] text-3xl font-bold">{name}</h1>

      {selfInitialAnswers?.length > 0 && (
        <OrganizationalDNA groupedLeadershipValues={groupedLeadershipValues} />
      )}
    </div>

    <div className="mt-[100px] flex-[2] space-y-8">
      {/* Personal Reflections */}
      {selfInitialAnswers?.length > 0 && (
        <div>
          <h2 className="text-[#161618] text-xl font-medium mb-6">
            Personal reflections (continued):
          </h2>

          <div className="space-y-6">
            {selfInitialAnswers.map((item: any, index: number) => (
              <div key={index}>
                <h3 className="text-[#161618] font-medium mb-2">
                  {item.question}
                </h3>
                <p className="text-[#58595a] text-sm leading-relaxed">
                  {item.responses[0]}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Themes */}
      {themes.length > 0 && (
        <div>
          <h2 className="text-[#161618] text-xl font-medium mb-6">
            {selfInitialAnswers?.length > 0
              ? 'Key themes (continued):'
              : 'As your coaching progressed, these were the key themes you explored:'}
          </h2>

          <div className="space-y-4">
            {themes.map((theme, index) => (
              <div key={index} className="bg-[#F6F8F8] p-4 rounded-lg">
                <p className="text-[#58595a] text-center">{theme}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {selfInitialAnswers?.length === 0 && (
        <div className="border-t border-dotted border-[#c7cbce] mt-6 pt-6">
          <OrganizationalDNA
            groupedLeadershipValues={groupedLeadershipValues}
          />
        </div>
      )}
    </div>

    <Footer otherCompanyLogo={otherCompanyLogo} />
  </div>
)

const OrganizationalDNA: React.FC<{
  groupedLeadershipValues: Record<string, string[]>
}> = ({ groupedLeadershipValues }) => {
  return (
    <div>
      <h3 className="text-[#161618] text-lg font-medium mb-4">
        Organizational DNA
      </h3>
      <p className="text-[#58595a] text-sm mb-6">
        Based on the set of Leadership Values defined by the firm, and
        co-designed with Hupo.
      </p>

      <div className="space-y-4">
        {Object.entries(groupedLeadershipValues)?.map(
          ([groupName, titles], index) => (
            <div key={groupName}>
              {groupName !== 'no-group' && (
                <h4 className="text-[#1c7aeb] font-medium">
                  {index + 1}. {groupName}
                </h4>
              )}
              <ul className="text-[#58595a] text-sm mt-1 list-disc list-inside space-y-1">
                {titles.map((title, titleIndex) => (
                  <li
                    key={`${groupName}-${titleIndex}`}
                    style={{ lineHeight: 1.5 }}
                  >
                    {title}
                  </li>
                ))}
              </ul>
            </div>
          )
        )}
      </div>
    </div>
  )
}

const Page3: React.FC<
  BasePageProps & {
    leadershipValues: string[]
    getChartData: (leadershipValue: string) => any[]
    selfInitialAnswers?: any[]
    userCompany?: string
  }
> = ({
  className,
  pageNo = 3,
  otherCompanyLogo,
  name,
  dateRange,
  leadershipValues,
  getChartData,
  selfInitialAnswers = [],
  userCompany,
}) => {
  const hasInitialData = selfInitialAnswers && selfInitialAnswers.length > 0

  const scoreLegend = getScoreLegend(userCompany)

  const getChartOptions = useCallback(
    (categoryName: string) => ({
      chart: {
        type: 'bar' as const,
        height: 230,
        toolbar: {
          show: false,
        },
        background: 'transparent',
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#333'],
        },
        formatter: function (val: number) {
          return val == 0 ? '0' : val.toFixed(1) // Fix: Always show 0 decimal places
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: GROUPS,
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        min: 0,
        max: scoreLegend.length,
        tickAmount: scoreLegend.length,
        labels: {
          style: { fontSize: '12px' },
          formatter: function (val: number) {
            return val == 0 ? '0' : val.toFixed(1) // Fix: Always show 0 decimal places
          },
        },
      },
      colors: hasInitialData
        ? ['#3b8cee', '#ff4b0a']
        : ['#3B8CEE', '#38A383', '#FF7E0D', '#8F5CF6'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
          borderRadius: 10,
          dataLabels: { position: 'top' },
          distributed: !hasInitialData,
        },
      },
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (val: number) => (val == 0 ? '0' : val.toFixed(1)), // Fix: Always show 0 decimal places
        },
      },
      legend: { show: false },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      title: {
        text: categoryName,
        align: 'center' as const,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          color: '#333',
        },
        margin: 5,
      },
    }),
    [hasInitialData]
  )

  const getFilteredChartData = useCallback(
    (category: string) => {
      const data = getChartData(category)
      if (!hasInitialData && Array.isArray(data)) {
        return data.filter((series) => series.name === 'End of Journey result')
      }
      return data
    },
    [getChartData, hasInitialData]
  )

  return (
    <div className={clsx('py-6', className)}>
      <PageIndicator pageNo={pageNo} />

      <PageHeader
        dateRange={dateRange}
        name={name}
        title={
          hasInitialData
            ? "See how your application of your organisation's leadership values has evolved:"
            : "See your current application of your organisation's leadership values:"
        }
      />

      <div className="bg-white pt-4 space-y-8">
        {hasInitialData && <ChartLegend />}

        <div className="grid grid-cols-2 gap-x-4">
          {leadershipValues.map((category, idx) => (
            <div key={idx} className="px-2">
              <Chart
                options={getChartOptions(category)}
                series={getFilteredChartData(category)}
                type="bar"
                height={230}
              />
            </div>
          ))}
        </div>

        <div className="mt-4 pb-4">
          <ScoreLegend userCompany={userCompany} />
        </div>
      </div>

      <Footer otherCompanyLogo={otherCompanyLogo} />
    </div>
  )
}

const Page4: React.FC<
  BasePageProps & {
    selectedValue: string
    chartData: any[]
    feedbackData: FeedbackItem[]
    selfInitialAnswers?: any[]
    userCompany?: string
  }
> = ({
  className,
  pageNo = 4,
  otherCompanyLogo,
  name,
  dateRange,
  selectedValue,
  chartData,
  feedbackData,
  selfInitialAnswers = [],
  userCompany,
}) => {
  const hasInitialData = selfInitialAnswers && selfInitialAnswers.length > 0

  const scoreLegend = getScoreLegend(userCompany)

  const getFilteredChartData = useMemo(() => {
    if (!hasInitialData && Array.isArray(chartData)) {
      return chartData.filter(
        (series) => series.name === 'End of Journey result'
      )
    }
    return chartData
  }, [chartData, hasInitialData])

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar' as const,
        height: 350,
        toolbar: { show: false },
        background: 'transparent',
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          colors: ['#333'],
        },
        formatter: function (val: number) {
          return val == 0 ? '0' : val.toFixed(1) // Fix: Always show 0 decimal places
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: GROUPS,
        labels: {
          style: {
            fontSize: '13px',
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        min: 0,
        max: scoreLegend.length,
        tickAmount: scoreLegend.length,
        labels: {
          style: { fontSize: '12px' },
          formatter: function (val: number) {
            return val == 0 ? '0' : val.toFixed(1) // Fix: Always show 0 decimal places
          },
        },
      },
      colors: hasInitialData
        ? ['#3b8cee', '#ff4b0a']
        : ['#3B8CEE', '#38A383', '#FF7E0D', '#8F5CF6'],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded',
          borderRadius: 10,
          dataLabels: { position: 'top' },
          distributed: !hasInitialData,
        },
      },
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (val: number) => (val == 0 ? '0' : val.toFixed(1)), // Fix: Always show 0 decimal places
        },
      },
      legend: { show: false },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    }),
    [hasInitialData]
  )

  return (
    <div className={clsx('py-6', className)}>
      <PageIndicator pageNo={pageNo} />

      <PageHeader
        dateRange={dateRange}
        name={name}
        title={`Values breakdown: ${selectedValue}`}
      />

      <div className="bg-white py-4 px-10">
        {hasInitialData && <ChartLegend />}

        <div className="mb-8">
          <Chart
            options={chartOptions}
            series={getFilteredChartData}
            type="bar"
            height={350}
          />
        </div>

        <div className="mb-4 pt-4">
          <ScoreLegend size="xs" userCompany={userCompany} />
        </div>

        {feedbackData.map((item, index) => (
          <div key={index} className={clsx(index > 0 && 'mt-6')}>
            <h2 className="text-xl font-bold mb-4">{item.role}</h2>

            {item.scoreComments.map((scoreComment, i) => (
              <div key={i} className="flex gap-3 items-start mb-4">
                <span className="text-[#3b8cee] font-bold text-xl">
                  {scoreComment.score}
                </span>
                <p className="text-gray-700 text-sm mt-1 leading-snug">
                  {scoreComment.comment}
                </p>
              </div>
            ))}
            {index < feedbackData.length - 1 && (
              <div className="border-t border-gray-200 my-6 border-dashed" />
            )}
          </div>
        ))}
      </div>

      <Footer otherCompanyLogo={otherCompanyLogo} />
    </div>
  )
}

const Page5: React.FC<
  BasePageProps & {
    personalReflections: PersonalReflection[]
  }
> = ({
  className,
  pageNo = 5,
  name,
  dateRange,
  personalReflections,
  otherCompanyLogo,
}) => (
  <div className={clsx('py-6', className)}>
    <PageIndicator pageNo={pageNo} />

    <PageHeader
      dateRange={dateRange}
      name={name}
      title="Here are your personal reflections after your coaching journey"
    />

    <div className="bg-white py-8 px-10">
      {personalReflections.map((item, index) => (
        <div key={index} className={clsx(index > 0 && 'mt-6')}>
          <p className="text-[#161618] text-lg font-bold mb-6 leading-snug">
            {item.question}
          </p>
          <p className="text-[#58595A] text-sm mb-6 leading-snug">
            {item.answer}
          </p>
          {index < personalReflections.length - 1 && (
            <div className="border-t border-gray-200 my-6 border-dashed" />
          )}
        </div>
      ))}
    </div>

    <Footer otherCompanyLogo={otherCompanyLogo} />
  </div>
)

// Additional reflections page component
const Page5AdditionalReflections: React.FC<
  BasePageProps & {
    additionalReflection: RespondentOpenEndedAnswer
  }
> = ({
  className,
  pageNo = 7,
  name,
  dateRange,
  additionalReflection,
  otherCompanyLogo,
}) => (
  <div className={clsx('py-6', className)}>
    <PageIndicator pageNo={pageNo} />

    <PageHeader
      dateRange={dateRange}
      name={name}
      title="Additional reflections from respondents"
    />

    <div className="bg-white py-8 px-10">
      <p className="text-[#161618] text-lg font-bold mb-6 leading-snug">
        {additionalReflection.title}
      </p>

      {additionalReflection.managers?.length > 0 && (
        <div>
          <p className="text-[#1C7AEB] font-semibold mb-2 leading-snug">
            Manager(s):
          </p>
          <ul className="list-disc list-inside space-y-2">
            {additionalReflection.managers.map((feedback, i) => (
              <li key={i} className="text-[#58595A] text-sm ml-4">
                {feedback}
              </li>
            ))}
          </ul>
        </div>
      )}

      {additionalReflection.managers?.length > 0 &&
        additionalReflection.peers?.length > 0 && (
          <div className="border-t border-gray-200 my-6 border-dashed" />
        )}

      {additionalReflection.peers?.length > 0 && (
        <div className="mt-4">
          <p className="text-[#1C7AEB] font-semibold mb-2 leading-snug">
            Peer(s):
          </p>
          <ul className="list-disc list-inside space-y-2">
            {additionalReflection.peers.map((feedback, i) => (
              <li key={i} className="text-[#58595A] text-sm ml-4">
                {feedback}
              </li>
            ))}
          </ul>
        </div>
      )}

      {additionalReflection.peers?.length > 0 &&
        additionalReflection.directReports?.length > 0 && (
          <div className="border-t border-gray-200 my-6 border-dashed" />
        )}

      {additionalReflection.directReports?.length > 0 && (
        <div className="mt-4">
          <p className="text-[#1C7AEB] font-semibold mb-2 leading-snug">
            Direct report(s):
          </p>
          <ul className="list-disc list-inside space-y-2">
            {additionalReflection.directReports.map((feedback, i) => (
              <li key={i} className="text-[#58595A] text-sm ml-4">
                {feedback}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    <Footer otherCompanyLogo={otherCompanyLogo} />
  </div>
)

const BackCover: React.FC<BasePageProps> = ({
  className,
  otherCompanyLogo,
  name,
  dateRange,
  otherCompanyLogoHeight = 14,
}) => (
  <div
    className={clsx(
      'bg-[url(/eojr-assets/cover-bg.png)] text-white p-8 flex flex-col justify-between',
      className
    )}
  >
    <div className="flex gap-4">
      <LogoText className="w-12 h-auto" color="#fff" />
      <img
        className={`w-${otherCompanyLogoHeight} h-auto object-contain`}
        src={otherCompanyLogo}
        alt="Company logo"
      />
    </div>

    <div className="flex-1 flex flex-col justify-center mx-8">
      <div className="mb-8">
        <div className="text-lg leading-5 tracking-[3px] mb-4">
          HUPO PERSPECTIVES
        </div>
        <h1 className="text-5xl font-bold mb-4 leading-tight">
          Your Hupo Coaching Journey
        </h1>
        <div className="text-xl mb-8">Personal Reflections & 360 Feedback</div>
      </div>

      <div className="mb-12">
        <div className="text-2xl font-bold mb-2 uppercase">{name}</div>
        <div className="text-xl font-bold">{dateRange}</div>
      </div>
    </div>

    <div className="text-center mt-8">
      <p className="text-sm">Thank you for your participation!</p>
      <p className="text-xs text-white mt-2">
        © {CURRENT_YEAR} Hupo. All rights reserved.
      </p>
    </div>
  </div>
)

// Main component
export const EndOfJourneyReportPDF = () => {
  const { userId } = useParams<{ userId: string }>()
  const [searchParams] = useSearchParams()

  useHideChatlio()

  const {
    data: endOfJourneyReport,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['end-of-journey-report-pdf', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required')

      const res = await fetch(
        `${
          import.meta.env.VITE_API_ENDPOINT
        }v2/user/end-journey/${userId}/report`
      )

      if (!res.ok) {
        throw new Error(`Failed to fetch report: ${res.statusText}`)
      }

      return res.json()
    },
    enabled: !!userId,
  })

  const formatDateRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return 'INVALID DATE RANGE'

    try {
      const start = parseISO(startDate)
      const end = parseISO(endDate)
      return `${format(start, 'MMM yyyy')} - ${format(
        end,
        'MMM yyyy'
      )}`.toUpperCase()
    } catch {
      return 'INVALID DATE RANGE'
    }
  }

  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      self: 'Self',
      managers: 'Manager(s)',
      peers: 'Peer(s)',
      directReports: 'Direct Report(s)',
    }
    return roleMap[role] || role
  }

  const groupLeadershipValues = (values: LeadershipValue[]) => {
    return values?.reduce((acc: Record<string, string[]>, item) => {
      const group = item.group || 'no-group'
      if (!acc[group]) {
        acc[group] = []
      }
      acc[group].push(item.title)
      return acc
    }, {})
  }

  const processedData = useMemo(() => {
    if (!endOfJourneyReport) return null

    const {
      user,
      startDate,
      endDate,
      completedSessionsCount,
      leadershipValues: leadershipValuesRaw,
      selfReported,
      respondentOpenEndedAnswers,
      chartData,
      selfReportedFeedback,
      selfAnswers,
    } = endOfJourneyReport

    const name = user?.profile?.name ?? 'Unknown User'
    const dateRange = formatDateRange(startDate, endDate)
    const themes =
      selfReported?.coachingThemes?.answer?.map((theme: any) => theme.title) ??
      []
    const leadershipValues =
      leadershipValuesRaw?.map((item: LeadershipValue) => item.title) ?? []
    const groupedLeadershipValues = groupLeadershipValues(leadershipValuesRaw)

    const getChartData = (leadershipValue: string): ChartDataSeries[] => [
      {
        name: 'Start of Journey result',
        data: chartData?.find(
          (item: ChartItem) => item.leadershipValue.title === leadershipValue
        )?.startOfJourney,
      },
      {
        name: 'End of Journey result',
        data: chartData?.find(
          (item: ChartItem) => item.leadershipValue.title === leadershipValue
        )?.endOfJourney,
      },
    ]

    const getFeedbackDataForValue = (selectedValue: string): FeedbackItem[] => {
      const valueData = leadershipValuesRaw?.find(
        (item: LeadershipValue) => item.title === selectedValue
      )

      if (!valueData?.scoreComments) return []

      const roleMap = new Map<string, Score[]>()

      Object.entries(valueData.scoreComments).forEach(
        ([role, scoreComments]) => {
          if (Array.isArray(scoreComments) && scoreComments.length > 0) {
            const displayRole = getRoleDisplayName(role)

            if (!roleMap.has(displayRole)) {
              roleMap.set(displayRole, [])
            }

            // Add comments, avoiding duplicates
            const existingComments = roleMap.get(displayRole)!
            scoreComments.forEach((comment) => {
              const isDuplicate = existingComments.some(
                (existing) =>
                  existing.score === comment.score &&
                  existing.comment === comment.comment
              )
              if (!isDuplicate) {
                existingComments.push(comment)
              }
            })
          }
        }
      )

      // Convert map to array
      return Array.from(roleMap.entries()).map(([role, scoreComments]) => ({
        role,
        scoreComments,
      }))
    }

    const personalReflections: PersonalReflection[] = [
      ...(selfReported?.openEnded ??
        (selfReported
          ? [
              selfReported.limitingBeliefs,
              selfReported.empoweringBeliefs,
            ].filter(Boolean)
          : [])),
      ...(selfReportedFeedback?.map((item: SelfReportedFeedback) => ({
        question: item.title,
        answer: item.feedback,
      })) || []),
    ]

    return {
      user,
      name,
      dateRange,
      completedSessionsCount: completedSessionsCount ?? 0,
      themes,
      leadershipValues,
      leadershipValuesRaw,
      groupedLeadershipValues,
      selfReported,
      respondentOpenEndedAnswers,
      chartData,
      personalReflections,
      getChartData,
      getFeedbackDataForValue, // Fix: Use the new function
      selfAnswers,
    }
  }, [endOfJourneyReport])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        Loading report...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        Error loading report: {error.message}
      </div>
    )
  }

  if (!processedData) {
    return (
      <div className="flex items-center justify-center h-64">
        No data available
      </div>
    )
  }

  const {
    user,
    name,
    dateRange,
    completedSessionsCount,
    themes,
    leadershipValues,
    groupedLeadershipValues,
    respondentOpenEndedAnswers,
    personalReflections,
    getChartData,
    getFeedbackDataForValue, // Fix: Use the new function
    selfAnswers,
  } = processedData

  let currentPageNumber = 1

  return (
    <div className="print-document">
      <FrontCover
        className="page"
        otherCompanyLogo={user?.company?.logoURL ?? ''}
        otherCompanyLogoHeight={user?.company?.logoHeight ?? 14}
        name={name}
        dateRange={dateRange}
        isDraft={searchParams.get('complete') === 'false'}
      />
      {/* Page2 with overflow handling */}
      {(() => {
        const page2Content = splitPage2Content(
          themes,
          selfAnswers,
          groupedLeadershipValues
        )

        return page2Content.map((pageData, pageIndex) => {
          if (pageIndex === 0) {
            // First Page2
            return (
              <Page2
                key="page2-main"
                pageNo={++currentPageNumber}
                className="page"
                otherCompanyLogo={user?.company?.logoURL ?? ''}
                name={name}
                dateRange={dateRange}
                completedSessions={completedSessionsCount}
                coach={user?.coach?.profile}
                themes={pageData.themes}
                groupedLeadershipValues={pageData.groupedLeadershipValues}
                selfInitialAnswers={pageData.selfInitialAnswers}
              />
            )
          } else {
            return (
              <Page2Overflow
                key={`page2-overflow-${pageIndex}`}
                pageNo={++currentPageNumber}
                className="page"
                otherCompanyLogo={user?.company?.logoURL ?? ''}
                name={name}
                dateRange={dateRange}
                themes={pageData.themes}
                selfInitialAnswers={pageData.selfInitialAnswers}
                groupedLeadershipValues={pageData.groupedLeadershipValues}
              />
            )
          }
        })
      })()}
      <Page3
        className="page bg-cover bg-[url(/eojr-assets/bg-image.svg)] bg-no-repeat bg-[#F6F8F8]"
        pageNo={++currentPageNumber} // Page 3
        otherCompanyLogo={user?.company?.logoURL ?? ''}
        name={name}
        userCompany={user?.company?._id}
        dateRange={dateRange}
        leadershipValues={leadershipValues.slice(0, 6)}
        getChartData={getChartData}
        selfInitialAnswers={selfAnswers}
      />
      {leadershipValues.length > 6 && (
        <Page3
          className="page bg-cover bg-[url(/eojr-assets/bg-image.svg)] bg-no-repeat bg-[#F6F8F8]"
          pageNo={++currentPageNumber} // Additional Page 3 for remaining values
          otherCompanyLogo={user?.company?.logoURL ?? ''}
          name={name}
          dateRange={dateRange}
          leadershipValues={leadershipValues.slice(6)}
          getChartData={getChartData}
          selfInitialAnswers={selfAnswers}
          userCompany={user?.company?._id}
        />
      )}
      {leadershipValues.map((value: any) => (
        <Fragment key={value}>
          <Page4
            className="page bg-cover bg-[url(/eojr-assets/bg-image.svg)] bg-no-repeat bg-[#F6F8F8]"
            pageNo={++currentPageNumber}
            selectedValue={value}
            feedbackData={getFeedbackDataForValue(value)} // ✅ Fixed: Use the correct function with the value parameter
            otherCompanyLogo={user?.company?.logoURL ?? ''}
            chartData={getChartData(value)}
            name={name}
            dateRange={dateRange}
            selfInitialAnswers={selfAnswers}
            userCompany={user?.company?._id}
          />
        </Fragment>
      ))}
      <Page5
        pageNo={++currentPageNumber}
        className="page bg-cover bg-[url(/eojr-assets/bg-image.svg)] bg-no-repeat bg-[#F6F8F8]"
        personalReflections={personalReflections}
        name={name}
        dateRange={dateRange}
        otherCompanyLogo={user?.company?.logoURL ?? ''}
      />
      {respondentOpenEndedAnswers?.map(
        (additionalReflection: any, index: number) => (
          <Page5AdditionalReflections
            key={index}
            pageNo={++currentPageNumber}
            className="page bg-cover bg-[url(/eojr-assets/bg-image.svg)] bg-no-repeat bg-[#F6F8F8]"
            additionalReflection={additionalReflection}
            name={name}
            dateRange={dateRange}
            otherCompanyLogo={user?.company?.logoURL ?? ''}
          />
        )
      )}
      <BackCover
        className="page"
        otherCompanyLogo={user?.company?.logoURL ?? ''}
        otherCompanyLogoHeight={user?.company?.logoHeight ?? 14}
        dateRange={dateRange}
        name={name}
      />
    </div>
  )
}
